import React, { useMemo, useState } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import { sanitizeHTML } from '../utils/security'
import { resolveTeamCategory } from '../utils/teamCategory'
import { Player, CareerHistoryEntry } from '../types'
import { ChevronLeft, Save, X, Users, Target, Activity, Youtube, FileText, Star, Plus, Trash2, History, Layout, User, Lock } from 'lucide-react'
import ImageUpload from './ImageUpload'
import { CUSTOM_COMPETITION_VALUE, collectCompetitionNames, getCompetitionPolicyViolation, inferCompetitionId } from '../utils/competitions'

const sanitizeCompetitionSelections = (competitions: string[]) =>
  Array.from(new Set(competitions.map((competition) => competition.trim()).filter(Boolean)))

interface EditPlayerProps {
  player: Player
  onSave: () => void
  onCancel: () => void
}

const POSITIONS = ['GK', 'CB', 'RB', 'LB', 'RWB', 'LWB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'SS', 'CF']
const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Injured', label: 'Injured' },
  { value: 'Loaned Out', label: 'Loaned Out' },
  { value: 'On-Loan', label: 'On-Loan' },
  { value: 'Trial', label: 'Trial' },
]

const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const POTENTIAL_LEVELS = [1, 2, 3, 4, 5]

type CompetitionPerformanceDraft = {
  id: string;
  competition_id: string;
  competition_name: string;
  appearance_date: string;
  opponent: string;
  venue: string;
  minutes: string;
  goals: string;
  assists: string;
  shots: string;
  key_passes: string;
  dribbles: string;
  tackles: string;
  rating: string;
  notes: string;
};

function EditPlayer({ player, onSave, onCancel }: EditPlayerProps) {
  const { updatePlayer, deletePlayer, clubs, competitions } = usePlayerStore()
  const { user } = useAuthStore()
  const isAdminRole = user?.role === 'admin'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [photoUrl, setPhotoUrl] = useState(player.photo_url || '')
  const [careerHistory, setCareerHistory] = useState<CareerHistoryEntry[]>(
    player.career_history?.length
      ? player.career_history
      : []
  )
  const [competitionPerformances, setCompetitionPerformances] = useState<CompetitionPerformanceDraft[]>(
    (player.competition_performances || []).map((performance) => ({
      id: performance.id,
      competition_id: performance.competition_id || '',
      competition_name: performance.competition_name || '',
      appearance_date: performance.appearance_date || '',
      opponent: performance.opponent || '',
      venue: performance.venue || '',
      minutes: String(performance.minutes || 0),
      goals: String(performance.goals || 0),
      assists: String(performance.assists || 0),
      shots: performance.shots === undefined || performance.shots === null ? '' : String(performance.shots),
      key_passes: performance.key_passes === undefined || performance.key_passes === null ? '' : String(performance.key_passes),
      dribbles: performance.dribbles === undefined || performance.dribbles === null ? '' : String(performance.dribbles),
      tackles: performance.tackles === undefined || performance.tackles === null ? '' : String(performance.tackles),
      rating: performance.rating === undefined || performance.rating === null ? '' : String(performance.rating),
      notes: performance.notes || '',
    }))
  )
  const [customCompetitionName, setCustomCompetitionName] = useState('')
  const competitionOptions = useMemo(
    () => collectCompetitionNames({ competitions, clubs, player }),
    [clubs, competitions, player]
  )
  const currentClub = user?.role === 'club' ? clubs.find((club) => club.id === user.id || (club as any).scout_id === user.id || club.scoutId === user.id) : null
  const isClubManagedPlayer = Boolean(
    currentClub && (
      (player.owner_club_id && player.owner_club_id === currentClub.id) ||
      (currentClub.name && (player.club || '').trim().toLowerCase() === currentClub.name.trim().toLowerCase())
    )
  )

  type PlayerFormData = {
    name: string;
    age: string;
    positions: string[];
    main_position: string;
    club: string;
    league: string;
    competitions: string[];
    state: string;
    lga: string;
    foot?: string;
    height?: string;
    weight?: string;
    goals: string;
    assists: string;
    apps: string;
    shots: string;
    keyPasses: string;
    dribbles: string;
    duels: string;
    minutesPlayed: string;
    potential: string;
    youtube: string;
    technical: string;
    physical: string;
    mental: string;
    notes: string;
    match_desc: string;
    status: string;
    owner_club_id?: string;
    jersey_number?: number;
    is_verified?: boolean;
    is_public?: boolean;
    has_agent?: boolean;
    agent_name?: string;
    agent_contact?: string;
  };

  const [formData, setFormData] = useState<PlayerFormData>({
    name: player.name,
    age: String(player.age),
    positions: player.positions || [],
    main_position: player.main_position || '',
    club: user?.role === 'club' ? (user.org || user.name || '') : player.club,
    league: player.league,
    competitions: player.competitions?.length ? player.competitions : (player.league ? [player.league] : []),
    state: player.state,
    lga: player.lga,
    foot: player.foot,
    height: player.height ? String(player.height) : '',
    weight: player.weight ? String(player.weight) : '',
    goals: String(player.goals || 0),
    assists: String(player.assists || 0),
    apps: String(player.apps || 0),
    shots: String(player.shots || 0),
    keyPasses: String(player.kp || 0),
    dribbles: String(player.dr || 0),
    duels: String(player.du || 0),
    minutesPlayed: String(player.mins || 0),
    potential: String(player.potential || 1),
    youtube: player.youtube || '',
    technical: String(player.technical || 5),
    physical: String(player.physical || 5),
    mental: String(player.mental || 5),
    notes: player.notes || '',
    match_desc: player.match_desc || '',
    status: player.status === 'Loaned' ? 'On-Loan' : (player.status || 'Active'),
    owner_club_id: user?.role === 'club' ? (currentClub?.id || player.owner_club_id) : player.owner_club_id,
    jersey_number: player.jersey_number,
    is_verified: player.is_verified,
    is_public: player.is_public,
    has_agent: player.has_agent ?? false,
    agent_name: player.agent_name || '',
    agent_contact: player.agent_contact || ''
  });
  const savedCompetitionNames = sanitizeCompetitionSelections(formData.competitions || [])
  const competitionSelections = sanitizeCompetitionSelections([
    ...savedCompetitionNames,
    customCompetitionName,
  ])
  const primaryCompetitionName = competitionSelections[0] || ''

  const updateCareerEntry = (id: string, field: keyof CareerHistoryEntry, value: string) => {
    setCareerHistory((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, [field]: value as any } : entry))
    )
  }

  const addCareerEntry = () => {
    setCareerHistory((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        club: '',
        years: '',
        goals: undefined,
        assists: undefined,
        transaction_fee: '',
      },
    ])
  }

  const removeCareerEntry = (id: string) => {
    setCareerHistory((prev) => prev.filter((entry) => entry.id !== id))
  }

  const addCompetitionPerformance = () => {
    const initialCompetition = primaryCompetitionName || competitionOptions[0] || player.league || ''
    setCompetitionPerformances((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        competition_id: inferCompetitionId(initialCompetition, competitions) || '',
        competition_name: initialCompetition,
        appearance_date: '',
        opponent: '',
        venue: '',
        minutes: '0',
        goals: '0',
        assists: '0',
        shots: '',
        key_passes: '',
        dribbles: '',
        tackles: '',
        rating: '',
        notes: '',
      },
    ])
  }

  const updateCompetitionPerformance = (id: string, field: keyof CompetitionPerformanceDraft, value: string) => {
    setCompetitionPerformances((prev) =>
      prev.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              [field]: value,
            }
          : entry
      )
    )
  }

  const removeCompetitionPerformance = (id: string) => {
    setCompetitionPerformances((prev) => prev.filter((entry) => entry.id !== id))
  }

  const toggleCompetitionSelection = (competitionName: string) => {
    setFormData((prev) => {
      const nextSelections = prev.competitions?.includes(competitionName)
        ? prev.competitions.filter((competition) => competition !== competitionName)
        : [...(prev.competitions || []), competitionName]

      const sanitizedSelections = sanitizeCompetitionSelections(nextSelections)

      return {
        ...prev,
        competitions: sanitizedSelections,
        league: sanitizedSelections[0] || '',
      }
    })
  }

  const addCustomCompetitionToProfile = () => {
    const competitionName = customCompetitionName.trim()
    if (!competitionName) return

    setFormData((prev) => {
      const sanitizedSelections = sanitizeCompetitionSelections([...(prev.competitions || []), competitionName])
      return {
        ...prev,
        competitions: sanitizedSelections,
        league: sanitizedSelections[0] || '',
      }
    })
    setCustomCompetitionName('')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this player? This action cannot be undone.')) return

    setLoading(true)
    setError('')

    try {
      await deletePlayer(player.id)
      onSave()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete player')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Player name is required')
      return
    }

    const competitionSelections = sanitizeCompetitionSelections([
      ...(formData.competitions || []),
      customCompetitionName,
    ])
    const competitionViolation = competitionSelections
      .map((competition) => getCompetitionPolicyViolation(competition))
      .find(Boolean)
    if (competitionViolation) {
      setError(competitionViolation)
      return
    }

    setLoading(true)
    try {
      const updatedPlayer: Partial<Player> = {
        name: sanitizeHTML(formData.name),
        age: parseInt(formData.age),
        positions: formData.positions,
        main_position: formData.main_position,
        club: user?.role === 'club' && currentClub?.name
          ? currentClub.name
          : sanitizeHTML(formData.club),
        league: sanitizeHTML(competitionSelections[0] || ''),
        competitions: competitionSelections.map((competition) => sanitizeHTML(competition)),
        state: formData.state,
        lga: sanitizeHTML(formData.lga),
        foot: formData.foot,
        height: formData.height ? parseFloat(formData.height) : undefined,
        weight: formData.weight ? parseInt(formData.weight) : undefined,
        goals: parseInt(formData.goals),
        assists: parseInt(formData.assists),
        apps: parseInt(formData.apps),
        shots: parseInt(formData.shots),
        kp: parseInt(formData.keyPasses),
        dr: parseInt(formData.dribbles),
        du: parseInt(formData.duels),
        mins: parseInt(formData.minutesPlayed),
        potential: parseInt(formData.potential),
        technical: parseInt(formData.technical),
        physical: parseInt(formData.physical),
        mental: parseInt(formData.mental),
        notes: sanitizeHTML(formData.notes),
        match_desc: sanitizeHTML(formData.match_desc),
        status: formData.status as Player['status'],
        career_history: careerHistory
          .filter((entry) => entry.club.trim() || entry.years.trim() || entry.goals !== undefined || entry.assists !== undefined || entry.transaction_fee?.trim())
          .map((entry) => ({
            ...entry,
            club: sanitizeHTML(entry.club),
            years: sanitizeHTML(entry.years),
            transaction_fee: entry.transaction_fee?.trim() ? sanitizeHTML(entry.transaction_fee.trim()) : undefined,
          })),
        competition_performances: competitionPerformances
          .filter((entry) => entry.competition_name.trim())
          .map((entry) => ({
            ...entry,
            competition_id: entry.competition_id || inferCompetitionId(entry.competition_name, competitions) || undefined,
            competition_name: entry.competition_name.trim(),
            appearance_date: entry.appearance_date || undefined,
            opponent: entry.opponent || undefined,
            venue: entry.venue || undefined,
            minutes: entry.minutes ? parseInt(entry.minutes) : 0,
            goals: entry.goals ? parseInt(entry.goals) : 0,
            assists: entry.assists ? parseInt(entry.assists) : 0,
            shots: entry.shots ? parseInt(entry.shots) : undefined,
            key_passes: entry.key_passes ? parseInt(entry.key_passes) : undefined,
            dribbles: entry.dribbles ? parseInt(entry.dribbles) : undefined,
            tackles: entry.tackles ? parseInt(entry.tackles) : undefined,
            rating: entry.rating ? parseFloat(entry.rating) : undefined,
            notes: sanitizeHTML(entry.notes || ''),
          })),
        youtube: formData.youtube,
        photo_url: photoUrl || undefined,
        team_category: isClubManagedPlayer ? resolveTeamCategory(player.team_category, parseInt(formData.age)) : player.team_category,
        has_agent: formData.has_agent,
        agent_name: formData.agent_name || undefined,
        agent_contact: formData.agent_contact || undefined,
        updated_at: new Date().toISOString()
      }

      // Only allow valid player fields in update
      const allowedFields: Array<keyof Player> = [
        'name', 'age', 'positions', 'main_position', 'club', 'league', 'competitions', 'state', 'lga', 'foot', 'height', 'weight', 'goals', 'assists', 'apps', 'shots', 'kp', 'dr', 'du', 'mins', 'potential', 'technical', 'physical', 'mental', 'notes', 'match_desc', 'status', 'career_history', 'competition_performances', 'youtube', 'photo_url', 'team_category', 'updated_at', 'owner_club_id', 'jersey_number', 'is_verified', 'is_public', 'has_agent', 'agent_name', 'agent_contact'
      ]
      const filteredUpdate = {} as Partial<Player>

      for (const key of allowedFields) {
        const value = updatedPlayer[key]
        if (value !== undefined) {
          ;(filteredUpdate as Record<string, unknown>)[key] = value
        }
      }

      // DEBUG: Log outgoing payload
      console.log('Update payload:', filteredUpdate)
      const isAdmin = user?.role === 'admin'
      const isOwner = player.scout === user?.email
      const isClubManager = user?.role === 'club' && isClubManagedPlayer
      
      // Only use service role for admins editing non-owned players
      const useServiceRole = isAdmin && !isOwner && !isClubManager
      
      await updatePlayer(player.id, filteredUpdate, { useServiceRole })
      onSave()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update player')
    } finally {
      setLoading(false)
    }
  }

  const canEdit =
    user?.role === 'admin' ||
    player.scout === user?.email ||
    (user?.role === 'club' && isClubManagedPlayer)

  if (!canEdit) {
    return (
      <div className="max-w-4xl mx-auto animate-in fade-in duration-500">
        <div className="bg-red-900/10 border border-red-900/20 rounded-[32px] p-12 text-center space-y-4">
          <X size={48} className="mx-auto text-red-500" />
          <p className="text-red-400 font-black uppercase tracking-widest text-xl">Access Denied</p>
          <p className="text-gray-500 text-sm max-w-md mx-auto">Only the original scout or an administrator has permission to modify this encrypted profile entry.</p>
          <button
            onClick={onCancel}
            className="mt-6 px-10 py-4 bg-red-600 hover:bg-red-700 rounded-full text-white font-black uppercase tracking-widest text-xs transition shadow-lg shadow-red-600/20"
          >
            Abort Modification
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <button
        onClick={onCancel}
        className="flex items-center gap-2 text-[#7cff01] hover:text-[#8eff1a] mb-8 transition font-black uppercase tracking-widest text-xs"
      >
        <ChevronLeft size={18} />
        Discard Changes
      </button>

      <div className="bg-[#021a1a] border border-white/5 shadow-2xl rounded-[32px] overflow-hidden">
        {/* Header */}
        <div className="p-8 border-b border-white/5 bg-white/2">
           <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter">
             Modifying <span className="text-[#7cff01]">{player.name}</span>
           </h2>
           <p className="text-gray-500 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Official Database Modification Protocol</p>
        </div>

        <div className="p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-900/10 border border-red-900/20 rounded-2xl text-red-400 text-xs font-bold uppercase tracking-widest text-center">
              ALERT: {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-12">
                        {/* Club Field - editable for scouts/admins, read-only for club users */}
                        <div className="space-y-6">
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Club</label>
                          <input
                            type="text"
                            name="club"
                            value={formData.club}
                            onChange={handleChange}
                            list="club-list"
                            className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                            disabled={user?.role === 'club' && !!currentClub?.name}
                          />
                          <datalist id="club-list">
                            {clubs.map(club => (
                              <option key={club.id} value={club.name}>{club.name}</option>
                            ))}
                          </datalist>
                        </div>
            {/* 1. Identity Module */}
            <div className="space-y-6">
               <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Users size={16} /> Identity Correction
               </h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                  />
                </div>
               </div>
            </div>

            {/* 2. Tactical Module */}
            <div className="space-y-6">
               <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Target size={16} /> Positional Data
               </h3>
               <div className="space-y-4">
                  <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Adjust Registered Positions</label>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {POSITIONS.map(pos => (
                      <button
                        key={pos}
                        type="button"
                        onClick={() => {
                          const newPositions = formData.positions.includes(pos)
                            ? formData.positions.filter((p: string) => p !== pos)
                            : [...formData.positions, pos]
                          setFormData(prev => ({ 
                            ...prev, 
                            positions: newPositions,
                            main_position: newPositions.includes(prev.main_position) ? prev.main_position : (newPositions[0] || '')
                          }))
                        }}
                        className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition border ${
                          formData.positions.includes(pos)
                            ? 'bg-[#7cff01] border-[#7cff01] text-black shadow-lg shadow-[#7cff01]/10'
                            : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/20'
                        }`}
                      >
                        {pos}
                      </button>
                    ))}
                  </div>
                  
                  {formData.positions.length > 0 && (
                    <div className="animate-in fade-in duration-300">
                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Primary Specialization</label>
                      <select
                        name="main_position"
                        value={formData.main_position}
                        onChange={handleChange}
                        className="w-full px-5 py-3 bg-[#7cff01]/10 border border-[#7cff01]/20 rounded-2xl text-[#7cff01] font-black uppercase tracking-widest outline-none transition"
                      >
                        {formData.positions.map((pos: string) => (
                          <option key={pos} value={pos} className="bg-gray-900">{pos}</option>
                        ))}
                      </select>
                    </div>
                  )}
               </div>
            </div>

            {/* 3. Competition Context */}
            <div className="space-y-6 p-8 bg-white/2 rounded-[32px] border border-white/5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Activity size={16} /> Competition Context
                </h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                  {savedCompetitionNames.length > 0 ? `${savedCompetitionNames.length} selected` : 'Optional'}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    Select one or more competitions the player participates in
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
                    {competitionOptions.map((competition) => {
                      const checked = formData.competitions.includes(competition)
                      return (
                        <label
                          key={competition}
                          className={`flex items-start gap-3 rounded-2xl border px-4 py-3 transition cursor-pointer ${
                            checked
                              ? 'bg-[#7cff01]/10 border-[#7cff01]/30'
                              : 'bg-white/5 border-white/5 hover:border-white/15'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCompetitionSelection(competition)}
                            className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent text-[#7cff01] focus:ring-[#7cff01]"
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-bold text-white truncate">{competition}</span>
                            <span className="block text-[11px] text-gray-500 mt-1">
                              {inferCompetitionId(competition, competitions) ? 'Competition directory entry' : 'Imported or custom competition'}
                            </span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-[24px] border border-white/5 bg-white/2 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3">Saved competitions</p>
                    {savedCompetitionNames.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {savedCompetitionNames.map((competition) => (
                          <span
                            key={competition}
                            className="inline-flex items-center rounded-full border border-[#7cff01]/20 bg-[#7cff01]/10 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-[#7cff01]"
                          >
                            {competition}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No competition selected yet.</p>
                    )}
                    <p className="text-sm text-gray-500 mt-3">
                      The first selected competition stays as the profile&apos;s primary label for legacy match recording.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">
                      Add a custom competition
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={customCompetitionName}
                        onChange={(e) => setCustomCompetitionName(e.target.value)}
                        placeholder="Type a competition name"
                        className="flex-1 px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                      />
                      <button
                        type="button"
                        onClick={addCustomCompetitionToProfile}
                        className="px-5 py-3 rounded-2xl bg-[#7cff01] text-black text-xs font-black uppercase tracking-widest hover:opacity-90 transition"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Assessment Module */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
               <div className="md:col-span-2 space-y-6">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                     <FileText size={16} /> Scout Report Correction
                  </h3>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={8}
                    className="w-full px-5 py-4 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-medium"
                  />
               </div>
               {isAdminRole ? (
                <div className="space-y-6">
                   <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                      <Star size={16} /> Assessment
                   </h3>
                   <div className="space-y-4">
                      {[
                        { label: 'Technical', name: 'technical' },
                        { label: 'Physical', name: 'physical' },
                        { label: 'Mental', name: 'mental' }
                      ].map(skill => (
                         <div key={skill.name}>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">{skill.label}</label>
                            <select name={skill.name} value={(formData as any)[skill.name]} onChange={handleChange} className="w-full px-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white font-bold outline-none">
                               {RATINGS.map(r => <option key={r} value={r} className="bg-gray-900">{r}/10</option>)}
                            </select>
                         </div>
                      ))}
                      <div>
                         <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">Potential</label>
                         <select name="potential" value={formData.potential} onChange={handleChange} className="w-full px-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white font-bold outline-none">
                            {POTENTIAL_LEVELS.map(level => (
                              <option key={level} value={level} className="bg-gray-900">{level} Star{level > 1 ? 's' : ''}</option>
                            ))}
                         </select>
                      </div>
                   </div>
                </div>
               ) : (
                <div className="space-y-6">
                   <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                      <Star size={16} /> Assessment
                   </h3>
                   <div className="border border-dashed border-gray-800 rounded-2xl p-5 bg-white/5 flex flex-col items-start gap-3">
                      <Lock size={18} className="text-gray-500" />
                      <p className="text-[11px] text-gray-400 leading-relaxed uppercase tracking-wider font-bold">
                         Talent assessment (technical, physical, mental & potential) is set exclusively by Scouta admins.
                      </p>
                   </div>
                </div>
               )}
            </div>

            {/* 5. Physical & Media Module */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               <div className="space-y-6">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                     <Activity size={16} /> Physical Spec
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                     <div>
                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Height (CM)</label>
                        <input type="number" name="height" value={formData.height} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-[#7cff01]/10 rounded-2xl text-white font-bold outline-none" />
                     </div>
                     <div>
                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Weight (KG)</label>
                        <input type="number" name="weight" value={formData.weight} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-[#7cff01]/10 rounded-2xl text-white font-bold outline-none" />
                     </div>
                  </div>
                  <select name="foot" value={formData.foot} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white font-bold outline-none">
                    <option value="Right" className="bg-gray-900">Right Footed</option>
                    <option value="Left" className="bg-gray-900">Left Footed</option>
                    <option value="Both" className="bg-gray-900">Ambidextrous</option>
                  </select>
               </div>
               <div className="space-y-6">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                     <Youtube size={16} /> Video Highlight
                  </h3>
                  <input type="url" name="youtube" value={formData.youtube} onChange={handleChange} placeholder="Paste Youtube Video link" className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none" />
                  <div className="p-4 bg-white/2 rounded-2xl border border-white/5">
                     <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-4">Official Profile Photo</label>
                     <ImageUpload onUpload={(url) => setPhotoUrl(url)} currentUrl={photoUrl} />
                  </div>
               </div>
            </div>

            {/* 6. Squad Status */}
            <div className="space-y-6">
               <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Layout size={16} /> Squad Status
               </h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Player Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white font-bold outline-none focus:border-[#7cff01] transition"
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value} className="bg-gray-900">
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-500 mt-2">
                      Use this to mark injuries, loan spells, and active squad availability.
                    </p>
                  </div>
               </div>
            </div>

            {/* 7. Agent / Manager */}
            <div className="space-y-6">
               <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <User size={16} /> Agent / Manager
               </h3>
               <p className="text-[11px] text-gray-500 -mt-3">
                  Declare representative details. Scouts and clubs will see this information.
               </p>
               <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, has_agent: true }))}
                    className={`flex-1 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition border ${
                      formData.has_agent === true
                        ? 'bg-green-500/10 border-green-500/30 text-green-400'
                        : 'bg-white/5 border-white/5 text-gray-500 hover:text-white'
                    }`}
                  >
                    Has Agent
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, has_agent: false, agent_name: '', agent_contact: '' }))}
                    className={`flex-1 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition border ${
                      formData.has_agent === false
                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                        : 'bg-white/5 border-white/5 text-gray-500 hover:text-white'
                    }`}
                  >
                    No Agent
                  </button>
               </div>
               {formData.has_agent && (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <div>
                     <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Agent Name</label>
                     <input
                       type="text"
                       value={formData.agent_name || ''}
                       onChange={(e) => setFormData(prev => ({ ...prev, agent_name: e.target.value }))}
                       placeholder="e.g. John Doe"
                       className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white font-bold outline-none focus:border-[#7cff01] transition"
                     />
                   </div>
                   <div>
                     <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Agent Contact</label>
                     <input
                       type="text"
                       value={formData.agent_contact || ''}
                       onChange={(e) => setFormData(prev => ({ ...prev, agent_contact: e.target.value }))}
                       placeholder="e.g. +234 801 234 5678"
                       className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white font-bold outline-none focus:border-[#7cff01] transition"
                     />
                   </div>
                 </div>
               )}
               {!formData.has_agent && (
                 <div className="p-4 bg-red-900/5 border border-red-500/20 rounded-2xl text-center">
                   <p className="text-red-400 text-xs font-black uppercase tracking-widest">NO AGENT</p>
                   <p className="text-gray-500 text-[10px] mt-1 uppercase tracking-widest">Scouts and clubs will see this player has no representative</p>
                 </div>
               )}
            </div>

            {/* 8. Historical Data */}
            <div className="space-y-6">
               <div className="flex items-center justify-between gap-4">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                     <History size={16} /> Historical Data
                  </h3>
                  <button
                    type="button"
                    onClick={addCareerEntry}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/5 text-white text-xs font-black uppercase tracking-widest hover:bg-white/10 transition"
                  >
                    <Plus size={14} />
                    Add Entry
                  </button>
               </div>

               {careerHistory.length === 0 ? (
                 <div className="rounded-2xl border border-dashed border-white/10 bg-white/2 p-6 text-center">
                   <p className="text-xs font-black uppercase tracking-widest text-gray-500">No historical transfers added yet</p>
                   <p className="text-[11px] text-gray-600 mt-2">Add previous clubs, year ranges, goals, assists, and fee data.</p>
                 </div>
               ) : (
                 <div className="space-y-4">
                   {careerHistory.map((entry) => (
                     <div key={entry.id} className="rounded-[24px] border border-white/5 bg-white/2 p-5 space-y-4">
                       <div className="flex items-center justify-between gap-3">
                         <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Career Record</p>
                         <button
                           type="button"
                           onClick={() => removeCareerEntry(entry.id)}
                           className="inline-flex items-center gap-2 text-red-400 text-[10px] font-black uppercase tracking-widest hover:text-red-300 transition"
                         >
                           <Trash2 size={14} />
                           Remove
                         </button>
                       </div>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
                           <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Club</label>
                           <input
                             type="text"
                             value={entry.club}
                             onChange={(e) => updateCareerEntry(entry.id, 'club', e.target.value)}
                             placeholder="Club name"
                             className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                           />
                         </div>
                         <div>
                           <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Years</label>
                           <input
                             type="text"
                             value={entry.years}
                             onChange={(e) => updateCareerEntry(entry.id, 'years', e.target.value)}
                             placeholder="2020-2023"
                             className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                           />
                         </div>
                         <div>
                           <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Goals</label>
                           <input
                             type="number"
                             value={entry.goals ?? ''}
                             onChange={(e) => updateCareerEntry(entry.id, 'goals', e.target.value)}
                             placeholder="0"
                             className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                           />
                         </div>
                         <div>
                           <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Assists</label>
                           <input
                             type="number"
                             value={entry.assists ?? ''}
                             onChange={(e) => updateCareerEntry(entry.id, 'assists', e.target.value)}
                             placeholder="0"
                             className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                           />
                         </div>
                         <div className="md:col-span-2">
                           <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Transaction Fee</label>
                           <input
                             type="text"
                             value={entry.transaction_fee || ''}
                             onChange={(e) => updateCareerEntry(entry.id, 'transaction_fee', e.target.value)}
                             placeholder="Optional, e.g. $50,000 or leave blank"
                             className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                           />
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
               )}
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Activity size={16} /> Competition Performance
                </h3>
                <button
                  type="button"
                  onClick={addCompetitionPerformance}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/5 text-white text-xs font-black uppercase tracking-widest hover:bg-white/10 transition"
                >
                  <Plus size={14} />
                  Add Record
                </button>
              </div>

              {competitionPerformances.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 bg-white/2 p-6 text-center">
                  <p className="text-xs font-black uppercase tracking-widest text-gray-500">No competition record added yet</p>
                  <p className="text-[11px] text-gray-600 mt-2">Attach performance numbers by competition for the player profile.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {competitionPerformances.map((entry) => (
                    <div key={entry.id} className="rounded-[24px] border border-white/5 bg-white/2 p-5 space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Competition Entry</p>
                        <button
                          type="button"
                          onClick={() => removeCompetitionPerformance(entry.id)}
                          className="inline-flex items-center gap-2 text-red-400 text-[10px] font-black uppercase tracking-widest hover:text-red-300 transition"
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Competition</label>
                          <select
                            value={competitionOptions.includes(entry.competition_name) ? entry.competition_name : CUSTOM_COMPETITION_VALUE}
                            onChange={(e) => {
                              const value = e.target.value
                              if (value === CUSTOM_COMPETITION_VALUE) {
                                updateCompetitionPerformance(entry.id, 'competition_name', '')
                                updateCompetitionPerformance(entry.id, 'competition_id', '')
                                return
                              }
                              updateCompetitionPerformance(entry.id, 'competition_name', value)
                              updateCompetitionPerformance(entry.id, 'competition_id', inferCompetitionId(value, competitions) || '')
                            }}
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white font-bold outline-none focus:border-[#7cff01] transition"
                          >
                            <option value="" className="bg-gray-900">Select competition</option>
                            {competitionOptions.map((competition) => (
                              <option key={competition} value={competition} className="bg-gray-900">
                                {competition}
                              </option>
                            ))}
                            <option value={CUSTOM_COMPETITION_VALUE} className="bg-gray-900">Custom competition</option>
                          </select>
                          {(!competitionOptions.includes(entry.competition_name) || !entry.competition_name) && (
                            <input
                              type="text"
                              value={entry.competition_name}
                              onChange={(e) => {
                                updateCompetitionPerformance(entry.id, 'competition_name', e.target.value)
                                updateCompetitionPerformance(entry.id, 'competition_id', inferCompetitionId(e.target.value, competitions) || '')
                              }}
                              placeholder="Type competition name"
                              className="mt-3 w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                            />
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Date</label>
                            <input
                              type="date"
                              value={entry.appearance_date}
                              onChange={(e) => updateCompetitionPerformance(entry.id, 'appearance_date', e.target.value)}
                              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white outline-none focus:border-[#7cff01] transition"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Rating</label>
                            <input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={entry.rating}
                              onChange={(e) => updateCompetitionPerformance(entry.id, 'rating', e.target.value)}
                              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white outline-none focus:border-[#7cff01] transition"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Opponent</label>
                          <input
                            type="text"
                            value={entry.opponent}
                            onChange={(e) => updateCompetitionPerformance(entry.id, 'opponent', e.target.value)}
                            placeholder="Opponent"
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Venue</label>
                          <input
                            type="text"
                            value={entry.venue}
                            onChange={(e) => updateCompetitionPerformance(entry.id, 'venue', e.target.value)}
                            placeholder="Stadium / venue"
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                          />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:col-span-2">
                          {[
                            { label: 'Minutes', key: 'minutes' },
                            { label: 'Goals', key: 'goals' },
                            { label: 'Assists', key: 'assists' },
                            { label: 'Shots', key: 'shots' },
                            { label: 'Key Passes', key: 'key_passes' },
                            { label: 'Dribbles', key: 'dribbles' },
                            { label: 'Tackles', key: 'tackles' },
                          ].map((stat) => (
                            <div key={stat.key}>
                              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">{stat.label}</label>
                              <input
                                type="number"
                                value={(entry as any)[stat.key]}
                                onChange={(e) => updateCompetitionPerformance(entry.id, stat.key as keyof CompetitionPerformanceDraft, e.target.value)}
                                className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white outline-none focus:border-[#7cff01] transition"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Performance Notes</label>
                          <textarea
                            value={entry.notes}
                            onChange={(e) => updateCompetitionPerformance(entry.id, 'notes', e.target.value)}
                            rows={3}
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                            placeholder="Short competition notes..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-8 border-t border-white/5 flex gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 py-5 bg-white/5 hover:bg-white/10 text-white rounded-[24px] font-black uppercase tracking-widest text-xs transition-colors"
              >
                Discard Edits
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-[2] py-5 bg-[#7cff01] hover:bg-[#8eff1a] disabled:opacity-50 text-black rounded-[24px] font-black uppercase tracking-[0.2em] shadow-xl shadow-[#7cff01]/20 transition-all transform hover:-translate-y-1 active:translate-y-0 flex items-center justify-center gap-3"
              >
                {loading ? <Activity className="animate-spin" size={20} /> : <Save size={20} />}
                {loading ? 'Committing Changes...' : 'Save Profile Corrections'}
              </button>
              {(user?.role === 'admin' || user?.role === 'club') && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex-1 py-5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-[24px] font-black uppercase tracking-widest text-xs transition-colors"
                >
                  Delete Player
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditPlayer
